/**
 * LearningHeader - 학습 페이지 헤더 컴포넌트
 *
 * LMSPlayer에서 추출된 재사용 가능한 헤더 컴포넌트.
 * 로고, 뒤로가기, 과정 제목, 레슨 제목, 완료 배지 등을 표시.
 */

import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { Link, useNavigate } from 'react-router-dom'
import { useTenantStore } from '@/store/tenantStore'
import {
    ArrowLeft,
    LogOut,
    CheckCircle,
    Menu,
    Layers,
    BookOpen,
    FolderOpen,
    FileText,
} from 'lucide-react'

// Utility functions
function getContentTypeIcon(contentType: string | undefined) {
    switch (contentType?.toLowerCase()) {
        case 'curriculum': return Layers
        case 'course': return BookOpen
        case 'module': return FolderOpen
        case 'lesson': return FileText
        default: return BookOpen
    }
}

function getContentTypeIconColor(contentType: string | undefined): string {
    switch (contentType?.toLowerCase()) {
        case 'curriculum': return 'text-purple-600'
        case 'course': return 'text-indigo-600'
        case 'module': return 'text-amber-600'
        case 'lesson': return 'text-gray-600'
        default: return 'text-indigo-600'
    }
}

export interface LearningHeaderProps {
    /** 사이드바 토글 핸들러 */
    onToggleSidebar: () => void
    /** 과정 제목 */
    courseTitle: string
    /** 콘텐츠 타입 (curriculum, course, module, lesson) */
    contentType?: string
    /** 현재 레슨 제목 (선택적) */
    lessonTitle?: string
    /** 완료 여부 */
    isCompleted?: boolean
    /** 뒤로가기 경로 (기본: /my/courses) */
    backPath?: string
    /** 추가 헤더 버튼 (선택적) */
    extraActions?: React.ReactNode
}

export function LearningHeader({
    onToggleSidebar,
    courseTitle,
    contentType,
    lessonTitle,
    isCompleted = false,
    backPath = '/my/courses',
    extraActions,
}: LearningHeaderProps) {
    const { t } = useTranslation('learner')
    const navigate = useNavigate()
    const { tenantInfo } = useTenantStore()

    const Icon = getContentTypeIcon(contentType)
    const iconColor = getContentTypeIconColor(contentType)

    return (
        <header className="sticky top-0 z-50 bg-white shadow-sm">
            <div className="flex justify-between items-center px-4 py-3 mx-auto max-w-full">
                <div className="flex items-center space-x-4">
                    {/* Sidebar Toggle */}
                    <button
                        onClick={onToggleSidebar}
                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600"
                    >
                        <Menu className="w-6 h-6" />
                    </button>

                    {/* Logo */}
                    <Link
                        to="/my/dashboard"
                        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
                    >
                        <img
                            src={tenantInfo?.logoUrl || '/lgcns-logo.png'}
                            alt={tenantInfo?.name || 'Quebon'}
                            className="w-auto h-7"
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = '/lgcns-logo.png'
                            }}
                        />
                        <span className="hidden font-semibold text-gray-900 sm:inline">
                            {tenantInfo?.displayName || tenantInfo?.name || 'Quebon B2B'}
                        </span>
                    </Link>

                    <div className="hidden w-px h-6 bg-gray-300 sm:block" />

                    {/* Course Title & Lesson */}
                    <div className="hidden sm:block">
                        <h1 className="flex gap-2 items-center text-sm font-semibold text-gray-900 md:text-base">
                            <Icon className={`w-4 h-4 ${iconColor}`} />
                            <ContentMetadataText text={courseTitle || t('learning.header.course')} className="min-w-0" />
                        </h1>
                        <p className="ml-6 text-xs text-gray-500">
                            {isCompleted ? t('learning.header.courseCompleted') : t('learning.header.inProgress')}
                            {lessonTitle && <> · <ContentMetadataText text={lessonTitle} /></>}
                        </p>
                    </div>

                    {/* Extra Actions */}
                    {extraActions}
                </div>

                {/* Right Side Actions */}
                <div className="flex items-center space-x-3">
                    {/* Completion Badge */}
                    {isCompleted && (
                        <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-full">
                            <CheckCircle className="mr-1 w-4 h-4" />
                            <span className="hidden sm:inline">{t('learning.header.courseCompleted')}</span>
                        </span>
                    )}

                    {/* Back Button (moved to right) */}
                    <button
                        onClick={() => navigate(backPath)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <span className="hidden sm:inline">{t('learning.header.classroom')}</span>
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </header>
    )
}
